
import { defineComponent } from 'vue'
import * as CryptoJS from 'crypto-js'
import { RefreshRight, DArrowRight } from '@element-plus/icons-vue'
import { getBehaviorCaptcha, checkBehaviorCaptcha } from '@/api/base'

/**
 * AES加密工具方法
 *
 * @param word 明文
 * @param keyStr 密钥
 */
function aesEncrypt(word: string, keyStr: string): string {
  if (!keyStr) return word
  const key = CryptoJS.enc.Utf8.parse(keyStr)
  const srcs = CryptoJS.enc.Utf8.parse(word)
  const encrypted = CryptoJS.AES.encrypt(srcs, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  })
  return encrypted.toString()
}

export default defineComponent({
  name: 'SliderVerify',
  components: {
    RefreshRight,
    DArrowRight
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'verified', 'close'],
  data() {
    return {
      visible: false,
      loading: false,
      captchaType: 'blockPuzzle' as 'blockPuzzle' | 'rotate',
      failCount: 0,
      originalImageBase64: '',
      jigsawImageBase64: '',
      token: '',
      secretKey: '',
      moveX: 0,
      isDragging: false,
      startX: 0,
      tipMsg: '',
      tipStatus: 'normal' as 'normal' | 'success' | 'error'
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal: boolean) {
        this.visible = !!newVal
        if (newVal) {
          this.resetFullState()
          this.loadCaptcha()
        }
      }
    }
  },
  beforeUnmount() {
    this.removeWindowListeners()
  },
  methods: {
    /**
     * 根据失败次数轮询计算验证码类型
     * 规则：两次拼图与两次旋转交替循环
     *
     * @param count 失败次数
     */
    getCaptchaTypeByFailCount(count: number): 'blockPuzzle' | 'rotate' {
      return Math.floor(count / 2) % 2 === 0 ? 'blockPuzzle' : 'rotate'
    },

    /**
     * 重置单次滑动状态
     */
    resetState() {
      this.moveX = 0
      this.isDragging = false
      this.tipMsg = ''
      this.tipStatus = 'normal'
    },

    /**
     * 重置所有状态与计数
     */
    resetFullState() {
      this.resetState()
      this.failCount = 0
      this.captchaType = this.getCaptchaTypeByFailCount(0)
      this.originalImageBase64 = ''
      this.jigsawImageBase64 = ''
    },

    /**
     * 加载验证码图片数据
     */
    async loadCaptcha() {
      this.loading = true
      this.resetState()
      const targetType = this.getCaptchaTypeByFailCount(this.failCount)
      if (targetType !== this.captchaType) {
        this.originalImageBase64 = ''
        this.jigsawImageBase64 = ''
      }
      try {
        const res = await getBehaviorCaptcha({
          captchaType: targetType
        })
        if (res?.repCode === '0000') {
          this.captchaType = targetType
          this.originalImageBase64 = res.repData?.originalImageBase64
            ? `data:image/png;base64,${res.repData.originalImageBase64}`
            : ''
          this.jigsawImageBase64 = res.repData?.jigsawImageBase64
            ? `data:image/png;base64,${res.repData.jigsawImageBase64}`
            : ''
          this.token = res.repData?.token || ''
          this.secretKey = res.repData?.secretKey || ''
        } else {
          this.tipMsg = res?.repMsg || '验证码加载失败'
          this.tipStatus = 'error'
        }
      } catch {
        this.tipMsg = '验证码服务异常'
        this.tipStatus = 'error'
      } finally {
        this.loading = false
      }
    },

    /**
     * 关闭弹窗
     */
    handleClose() {
      this.visible = false
      this.$emit('update:modelValue', false)
      this.$emit('close')
      this.resetFullState()
    },

    /**
     * 校验失败处理及轮询切换验证方式
     */
    handleCheckFail() {
      this.failCount++
      this.moveX = 0
      this.tipStatus = 'error'
      this.tipMsg = '验证失败，请重试'

      setTimeout(() => {
        this.loadCaptcha()
      }, 800)
    },

    /**
     * 提交滑块拼图校验
     */
    async submitSliderCheck() {
      this.loading = true
      try {
        const point = { x: Math.round(this.moveX), y: 5.0 }
        const pointStr = JSON.stringify(point)
        const pointJson = this.secretKey
          ? aesEncrypt(pointStr, this.secretKey)
          : pointStr

        const res = await checkBehaviorCaptcha({
          captchaType: 'blockPuzzle',
          pointJson: pointJson,
          token: this.token
        })

        if (res?.repCode === '0000') {
          this.tipMsg = '验证成功'
          this.tipStatus = 'success'
          const rawVerification = `${this.token}---${pointStr}`
          const captchaVerification = this.secretKey
            ? aesEncrypt(rawVerification, this.secretKey)
            : rawVerification

          setTimeout(() => {
            this.$emit('verified', captchaVerification)
            this.handleClose()
          }, 500)
          return
        }

        this.handleCheckFail()
      } catch {
        this.handleCheckFail()
      } finally {
        this.loading = false
      }
    },

    /**
     * 提交图片旋转校验
     */
    async submitRotateCheck() {
      this.loading = true
      try {
        const maxMove = 310 - 47
        const userAngle = Math.round((this.moveX / maxMove) * 360) % 360
        const point = { x: userAngle, y: 0 }
        const pointStr = JSON.stringify(point)
        const pointJson = this.secretKey
          ? aesEncrypt(pointStr, this.secretKey)
          : pointStr

        const res = await checkBehaviorCaptcha({
          captchaType: 'rotate',
          pointJson: pointJson,
          token: this.token
        })

        if (res?.repCode === '0000') {
          this.tipMsg = '验证成功'
          this.tipStatus = 'success'
          const rawVerification = `${this.token}---${pointStr}`
          const captchaVerification = this.secretKey
            ? aesEncrypt(rawVerification, this.secretKey)
            : rawVerification

          setTimeout(() => {
            this.$emit('verified', captchaVerification)
            this.handleClose()
          }, 500)
          return
        }

        this.handleCheckFail()
      } catch {
        this.handleCheckFail()
      } finally {
        this.loading = false
      }
    },

    /**
     * 统一拖拽松开处理
     */
    onVerifyEnd() {
      if (!this.isDragging) return
      this.isDragging = false
      this.removeWindowListeners()
      if (this.captchaType === 'blockPuzzle') {
        this.submitSliderCheck()
      } else {
        this.submitRotateCheck()
      }
    },

    /**
     * 鼠标移动事件
     */
    onWindowMouseMove(e: MouseEvent) {
      if (!this.isDragging) return
      e.preventDefault()
      const deltaX = e.clientX - this.startX
      const maxMove = 310 - 47
      this.moveX = Math.max(0, Math.min(deltaX, maxMove))
    },

    /**
     * 鼠标按下滑块把手
     */
    onHandleMouseDown(e: MouseEvent) {
      if (this.loading || this.tipStatus === 'success') return
      e.preventDefault()
      e.stopPropagation()
      this.startX = e.clientX
      this.isDragging = true
      window.addEventListener('mousemove', this.onWindowMouseMove)
      window.addEventListener('mouseup', this.onVerifyEnd)
    },

    /**
     * 触摸移动事件
     */
    onWindowTouchMove(e: TouchEvent) {
      if (!this.isDragging) return
      e.preventDefault()
      const touch = e.touches[0]
      const deltaX = touch.clientX - this.startX
      const maxMove = 310 - 47
      this.moveX = Math.max(0, Math.min(deltaX, maxMove))
    },

    /**
     * 触摸按下滑块把手
     */
    onHandleTouchStart(e: TouchEvent) {
      if (this.loading || this.tipStatus === 'success') return
      e.stopPropagation()
      const touch = e.touches[0]
      this.startX = touch.clientX
      this.isDragging = true
      window.addEventListener('touchmove', this.onWindowTouchMove, { passive: false })
      window.addEventListener('touchend', this.onVerifyEnd)
    },

    /**
     * 移除全局窗口监听
     */
    removeWindowListeners() {
      window.removeEventListener('mousemove', this.onWindowMouseMove)
      window.removeEventListener('mouseup', this.onVerifyEnd)
      window.removeEventListener('touchmove', this.onWindowTouchMove)
      window.removeEventListener('touchend', this.onVerifyEnd)
    },

    /**
     * 手动刷新
     */
    handleRefresh() {
      if (this.loading) return
      this.loadCaptcha()
    }
  }
})
